import type { SelectChangeEvent, SelectProps } from "@mui/material"
import { Box, Checkbox, Chip, FormControl, MenuItem, Select, Typography } from "@mui/material"
import { CloseFilledOutlined, MoreHorizOutlined } from "@mui-symbols-material/w300"
import { useCallback, useEffect, useRef, useState } from "react"
import { useController, useFormContext } from "react-hook-form"
import { twMerge } from "tailwind-merge"

import { FIELD_LABEL_CLASSES, MENU_PROPS, SELECT_STYLING_PROPS } from "@/constants/forms"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"

export enum ChipBackground {
  PRIMARY = "bg-primary-100",
  YELLOW = "bg-tertiary-yellow-300",
}

interface SelectDialogContentProps<T> {
  options: T[]
  title: string
  label: string
  controlName: string
  idField: keyof T
  nameField: keyof T
  chipBackground?: ChipBackground
  placeholder?: string
  fetching?: boolean
  onLoadMore?: () => void
}

export const SelectDialogContent = <T,>({
  options,
  title,
  label,
  controlName,
  idField,
  nameField,
  chipBackground = ChipBackground.PRIMARY,
  placeholder,
  fetching = false,
  onLoadMore,
}: SelectDialogContentProps<T>): React.ReactElement => {
  const { control } = useFormContext()
  const { field } = useController({
    name: controlName,
    control,
  })

  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const menuPaperRef = useRef<HTMLDivElement>(null)
  const [visibleChips, setVisibleChips] = useState<string[]>([])
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false)

  const updateVisibleChips = useCallback(() => {
    if (measureRef.current && containerRef.current) {
      const containerWidth = containerRef.current.clientWidth - 40 // 32 is the width of the MoreHorizOutlined icon
      let totalWidth = 0
      const visibleChipsArray: string[] = []

      Array.from(measureRef.current.children).forEach((chip, index) => {
        const chipWidth = (chip as HTMLElement).offsetWidth
        if (totalWidth + chipWidth <= containerWidth) {
          visibleChipsArray.push(field.value[index][idField] as string)
          totalWidth += chipWidth
        }
      })
      setVisibleChips(visibleChipsArray)
      setIsOverflowing(visibleChipsArray.length < field.value.length)
    }
  }, [field.value, idField])

  useEffect(() => {
    updateVisibleChips()
  }, [field.value, updateVisibleChips])

  const handleLoadMore = useCallback(() => onLoadMore?.(), [onLoadMore])

  const handleMenuScroll = useScrollLoadMore({
    fetching,
    loadMore: handleLoadMore,
    scrollContainerRef: menuPaperRef,
  })

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const selectedIds = event.target.value as string[]
    const selectedOptions = selectedIds
      .map(
        (id) =>
          options.find((option) => String(option[idField]) === id) ??
          field.value.find((option: T) => String(option[idField]) === id)
      )
      .filter(Boolean)
    field.onChange(selectedOptions)
  }

  const handleDelete = (valueId: string) => {
    field.onChange(field.value.filter((option: T) => option[idField] !== valueId))
  }

  return (
    <form>
      <Typography variant='body1' fontWeight={700} className='leading-6 tracking-[0.15px] text-gray-700'>
        {title}
      </Typography>
      <Box className='mt-4 flex min-h-10 flex-wrap gap-2'>
        {field.value?.map((option: T) => (
          <Chip
            key={String(option[idField])}
            label={String(option[nameField])}
            className={twMerge(chipBackground, "h-6 rounded-sm text-xs text-gray-900")}
            onDelete={() => handleDelete(String(option[idField]))}
            deleteIcon={
              <CloseFilledOutlined
                className='size-4 text-gray-900'
                onClick={() => handleDelete(String(option[idField]))}
              />
            }
          />
        ))}
      </Box>
      <FormControl fullWidth className='mt-8'>
        <label className={FIELD_LABEL_CLASSES}>{label}</label>
        <Select
          {...(SELECT_STYLING_PROPS as Partial<SelectProps<string[]>>)}
          id={`${controlName}-select`}
          multiple
          renderValue={() => (
            <Box className='flex items-center overflow-hidden' ref={containerRef}>
              <Box className='flex flex-nowrap gap-1'>
                {placeholder && visibleChips.length === 0 && <span className='text-gray-500'>{placeholder}</span>}
                {visibleChips.map((optionId) => {
                  const option = options.find((option) => String(option[idField]) === optionId)
                  return option ? (
                    <Chip
                      key={optionId}
                      label={String(option[nameField])}
                      className={twMerge(chipBackground, "mr-1 h-6 rounded-sm text-xs text-gray-900")}
                    />
                  ) : null
                })}
              </Box>
              {isOverflowing && (
                <Box className={twMerge(chipBackground, "mx-1 flex size-6 items-center justify-center rounded-sm")}>
                  <MoreHorizOutlined className='ml-0 shrink-0' />
                </Box>
              )}
            </Box>
          )}
          value={field.value.map((option: T) => String(option[idField]))}
          onChange={handleChange}
          MenuProps={{
            ...MENU_PROPS,
            PaperProps: {
              ...MENU_PROPS.PaperProps,
              ref: menuPaperRef,
              onScroll: onLoadMore ? handleMenuScroll : undefined,
            },
          }}
        >
          {options.map((option) => (
            <MenuItem key={String(option[idField])} value={String(option[idField])}>
              <Checkbox checked={field.value.some((value: T) => String(value[idField]) === String(option[idField]))} />
              {String(option[nameField])}
            </MenuItem>
          ))}
        </Select>
        <Box className='invisible absolute' ref={measureRef}>
          {field.value.map((value: T) => (
            <Chip
              key={String(value[idField])}
              label={String(value[nameField])}
              className={twMerge(chipBackground, "mr-1 h-6 rounded-sm text-xs text-gray-900")}
            />
          ))}
        </Box>
      </FormControl>
    </form>
  )
}
